import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { toast } from 'sonner'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import { Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import Field from '@/components/ui/Field'
import FieldStatusMessage from '@/components/ui/FieldStatusMessage'
import { Description } from '@/components/ui/Description'
import useUpdateProfile from '@/services/api/auth/useUpdateProfile'
import { Switch } from '@/components/ui/switch'

export function ProfileSection() {
  const { data: userData, status } = useGetCurrentUser()
  const [isEditing, setIsEditing] = useState(false)

  const schema = z.object({
    name: z.string().min(1, 'The name field is required.'),
    lastName: z.string().min(1, 'The last name field is required.'),
  })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: userData.name,
      lastName: userData.last_name,
    },
  })

  const { setError, formState, handleSubmit } = form

  const { action: updateProfile, status: updateProfileStatus } =
    useUpdateProfile({
      setError,
      clientServerFieldMapping: {
        name: 'name',
        lastName: 'last_name',
      },
    })

  const { errors } = formState

  const handleEdit = () => {
    setIsEditing(true)
  }

  const handleCancel = () => {
    setIsEditing(false)
    form.reset()
  }

  // Saved straight away, independent of the name edit mode.
  const toggleMarketing = (checked: boolean) =>
    updateProfile({
      name: userData.name,
      lastName: userData.last_name,
      marketingOptIn: checked,
    })

  const handleSave = async (values: any) => {
    await updateProfile({ name: values.name, lastName: values.lastName })
    setIsEditing(false)
  }

  return (
    <form onSubmit={handleSubmit(handleSave)} className="space-y-6">
      {status === 'pending' && (
        <div className="flex items-center justify-center">
          <Loader2 className="size-6 animate-spin" />
        </div>
      )}
      <div>
        <h2 className="mb-2">Profile Information</h2>
        <p className="mb-4 text-gray-600">Update your personal information.</p>
      </div>

      <div className="grid grid-cols-1 gap-field-gap md:grid-cols-2">
        <Field
          statusText={errors?.name?.message?.toString()}
          status={errors?.name ? 'error' : null}
        >
          <Label htmlFor="name">First Name</Label>
          <Input
            id="name"
            disabled={!isEditing}
            {...form.register('name')}
            className="disabled:text-txt-primary"
            placeholder="Enter your first name"
          />
          <FieldStatusMessage />
        </Field>

        <Field
          statusText={errors?.lastName?.message?.toString()}
          status={errors?.lastName ? 'error' : null}
        >
          <Label htmlFor="lastName">Last Name</Label>

          <Input
            id="lastName"
            disabled={!isEditing}
            {...form.register('lastName')}
            className="disabled:text-txt-primary"
            placeholder="Enter your last name"
          />

          <FieldStatusMessage />
        </Field>
      </div>

      <Field>
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          value={userData.email}
          disabled
          className="disabled:text-txt-primary"
          placeholder="Enter your email address"
        />
        <Description>
          To cahnge your email address, use the Email section in the sidebar.
        </Description>
      </Field>

      <Field>
        <div className="flex items-start justify-between gap-4">
          <div>
            <Label htmlFor="marketing-opt-in">Product news and offers</Label>
            <Description>
              Occasional emails about new features, tips and offers. Billing and
              trial reminders are always sent.
            </Description>
          </div>
          <Switch
            id="marketing-opt-in"
            checked={!!userData.marketing_opt_in}
            onCheckedChange={toggleMarketing}
            disabled={updateProfileStatus.status === 'pending'}
          />
        </div>
      </Field>

      <div className="flex gap-3">
        {!isEditing ? (
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              handleEdit()
            }}
          >
            Edit Profile
          </Button>
        ) : (
          <>
            <Button
              type="submit"
              disabled={updateProfileStatus.status === 'pending'}
            >
              {updateProfileStatus.status === 'pending'
                ? 'Saving...'
                : 'Save Changes'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={(e) => {
                e.preventDefault()
                handleCancel()
              }}
              disabled={updateProfileStatus.status === 'pending'}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
    </form>
  )
}
